import { DOMAIN_ERROR_CODES, DomainError, TRANSPORT_ERROR_CODES, TransportError, } from "@econ-v1/domain";
export const OWNER_VIEW_HTTP_OPERATIONS = {
    capabilityInvoke: "POST /api/v2/system/capabilities/invoke",
    systemMetrics: "GET /api/v2/system/metrics",
};
export const OWNER_VIEW_CAPABILITIES = {
    feed: "core.notifications.list",
    home: "core.system.info",
    peers: "core.network.list_peer_offerings",
};
/**
 * Browser-side owner-view repository.
 *
 * The adapter deliberately knows only the HomeNodeClient port. Authentication,
 * authorization headers, transport selection, and HTTP envelope handling stay
 * in the selected HomeNodeClient implementation.
 */
export class HomeNodeOwnerViewRepository {
    #capabilities;
    #homeNode;
    constructor(homeNode, options = {}) {
        this.#homeNode = homeNode;
        this.#capabilities = {
            ...OWNER_VIEW_CAPABILITIES,
            ...options.capabilities,
        };
    }
    async readHomeSummary() {
        const response = await this.#invokeCapability(this.#capabilities.home, {});
        return parseHomeSummary(response);
    }
    async listPeers(params) {
        const query = parsePeerQuery(params);
        const response = await this.#invokeCapability(this.#capabilities.peers, compact({
            limit: query.limit,
            status: query.status,
        }));
        const peers = parsePeers(response);
        return peers
            .filter((peer) => query.status === undefined || peer.status === query.status)
            .slice(0, query.limit ?? 50);
    }
    async listFeed(params) {
        const query = parseFeedQuery(params);
        const response = await this.#invokeCapability(this.#capabilities.feed, compact({
            before: query.before,
            limit: query.limit,
            unread_only: query.unreadOnly,
        }));
        const items = parseFeedItems(response);
        return items
            .filter((item) => query.unreadOnly !== true || !item.read)
            .filter((item) => query.before === undefined || item.createdAt < query.before)
            .slice(0, query.limit ?? 50);
    }
    async readOperationalSummary() {
        const response = await this.#invoke({
            operation: OWNER_VIEW_HTTP_OPERATIONS.systemMetrics,
            payload: {},
        });
        return parseOperationalSummary(response, this.#homeNode);
    }
    async #invokeCapability(capability, payload) {
        return await this.#invoke({
            operation: OWNER_VIEW_HTTP_OPERATIONS.capabilityInvoke,
            payload: { capability, payload },
        });
    }
    async #invoke(request) {
        try {
            return await this.#homeNode.invoke(request);
        }
        catch (error) {
            if (isClientError(error))
                throw error;
            throw new TransportError({
                code: "unavailable",
                message: "Home node owner-view request failed",
            });
        }
    }
}
function isClientError(value) {
    if (value instanceof DomainError || value instanceof TransportError)
        return true;
    if (!isRecord(value) || typeof value.kind !== "string" || typeof value.code !== "string")
        return false;
    return value.kind === "domain"
        ? DOMAIN_ERROR_CODES.includes(value.code)
        : value.kind === "transport" && TRANSPORT_ERROR_CODES.includes(value.code);
}
export function createHomeNodeOwnerViewRepository(homeNode, options = {}) {
    return new HomeNodeOwnerViewRepository(homeNode, options);
}
/** Backwards-compatible short factory name for adapter consumers. */
export const createHomeNodeOwnerRepository = createHomeNodeOwnerViewRepository;
function parseHomeSummary(value) {
    const record = responseRecord(value, "home summary");
    const lastSyncedAt = optionalTimestamp(record, "last_synced_at", "home summary");
    return {
        displayName: requiredString(record, "display_name", "home summary"),
        homeNodeId: requiredString(record, "home_node_id", "home summary"),
        ...(lastSyncedAt === undefined ? {} : { lastSyncedAt }),
        status: parseStatus(record.status, ["offline", "online", "unknown"], "home summary", "status"),
    };
}
function parsePeers(value) {
    const record = responseRecord(value, "peer directory");
    const peers = requiredArray(record, "peers", "peer directory");
    return peers.map((peer, index) => {
        const row = responseRecord(peer, `peer directory peers[${index}]`);
        const id = firstString(row, ["id", "peer_node_id"], `peer directory peers[${index}]`);
        const displayName = firstString(row, ["display_name", "name", "peer_node_id", "id"], `peer directory peers[${index}]`);
        const lastSeenAt = firstOptionalTimestamp(row, ["last_seen_at", "fetched_at"], `peer directory peers[${index}]`);
        const status = row.status === undefined
            ? row.last_error === undefined || row.last_error === null || row.last_error === ""
                ? "online"
                : "offline"
            : parseStatus(row.status, ["offline", "online", "unknown"], "peer directory", `peers[${index}].status`);
        return {
            displayName,
            id,
            ...(lastSeenAt === undefined ? {} : { lastSeenAt }),
            status,
        };
    });
}
function parseFeedItems(value) {
    const record = responseRecord(value, "feed");
    const rawItems = record.items ?? record.notifications;
    if (!Array.isArray(rawItems))
        throw malformed("feed", "items");
    return rawItems.map((item, index) => {
        const row = responseRecord(item, `feed items[${index}]`);
        const id = requiredStringFromAliases(row, ["id"], `feed items[${index}]`);
        const body = requiredStringFromAliases(row, ["body", "content"], `feed items[${index}]`);
        const createdAt = firstString(row, ["created_at", "createdAt"], `feed items[${index}]`);
        const title = firstString(row, ["title", "notification_type"], `feed items[${index}]`);
        const kind = row.kind === undefined
            ? "system"
            : parseStatus(row.kind, ["activity", "message", "system"], "feed", `items[${index}].kind`);
        const read = row.read === undefined
            ? parseReadAt(row.read_at, `feed items[${index}].read_at`)
            : requiredBoolean(row, "read", `feed items[${index}]`);
        return { body, createdAt, id, kind, read, title };
    });
}
function parseOperationalSummary(value, homeNode) {
    const record = responseRecord(value, "system metrics");
    const operational = isRecord(record.operational)
        ? record.operational
        : hasOperationalFields(record)
            ? record
            : undefined;
    if (operational !== undefined) {
        return {
            outboxDepth: nonNegativeInteger(operational.outbox_depth, "system metrics", "outbox_depth"),
            storage: parseStatus(operational.storage, ["blocked", "ready", "recovering"], "system metrics", "storage"),
            sync: parseStatus(operational.sync, ["current", "stale", "syncing"], "system metrics", "sync"),
            transport: operational.transport === undefined
                ? homeNode.state.path
                : parseStatus(operational.transport, ["iroh-relay", "lan-direct", "lan-webtransport", "offline", "wan-direct", "webrtc-direct"], "system metrics", "transport"),
        };
    }
    parseSystemMetrics(record);
    return {
        outboxDepth: 0,
        storage: "ready",
        sync: homeNode.state.status === "connected" ? "current" : "stale",
        transport: homeNode.state.path,
    };
}
function parseSystemMetrics(record) {
    if (!Number.isInteger(record.id) || record.id < 0)
        throw malformed("system metrics", "id");
    requiredString(record, "timestamp", "system metrics");
    parseMetricGroup(record.cpu, ["usage_percent", "history"], "system metrics cpu");
    parseMetricGroup(record.memory, ["used_bytes", "total_bytes", "usage_percent"], "system metrics memory");
    parseMetricGroup(record.disk, ["used_bytes", "total_bytes", "usage_percent"], "system metrics disk");
    parseMetricGroup(record.network, ["upload_mbps", "download_mbps", "upload_history", "download_history"], "system metrics network");
}
function parseMetricGroup(value, fields, label) {
    const record = responseRecord(value, label);
    for (const field of fields) {
        const actual = record[field];
        if (field.endsWith("history")) {
            if (!Array.isArray(actual) || actual.some((entry) => typeof entry !== "number" || !Number.isFinite(entry))) {
                throw malformed(label, field);
            }
        }
        else if (typeof actual !== "number" || !Number.isFinite(actual)) {
            throw malformed(label, field);
        }
    }
}
function parsePeerQuery(params) {
    const limit = parseLimit(params?.limit, "peer directory");
    const status = params?.status;
    if (status !== undefined && !["offline", "online", "unknown"].includes(status)) {
        throw new DomainError({ code: "invalid_request", message: "Invalid peer directory query parameters" });
    }
    return { ...(limit === undefined ? {} : { limit }), ...(status === undefined ? {} : { status }) };
}
function parseFeedQuery(params) {
    const before = params?.before;
    if (before !== undefined && typeof before !== "string") {
        throw new DomainError({ code: "invalid_request", message: "Invalid feed query parameters" });
    }
    const limit = parseLimit(params?.limit, "feed");
    const unreadOnly = params?.unreadOnly;
    if (unreadOnly !== undefined && typeof unreadOnly !== "boolean") {
        throw new DomainError({ code: "invalid_request", message: "Invalid feed query parameters" });
    }
    return {
        ...(before === undefined ? {} : { before }),
        ...(limit === undefined ? {} : { limit }),
        ...(unreadOnly === undefined ? {} : { unreadOnly }),
    };
}
function parseLimit(value, label) {
    if (value === undefined)
        return undefined;
    if (!Number.isInteger(value) || value < 1 || value > 100) {
        throw new DomainError({ code: "invalid_request", message: `Invalid ${label} query parameters` });
    }
    return value;
}
function parseReadAt(value, path) {
    if (value === null)
        return false;
    if (typeof value === "string" && value.length > 0)
        return true;
    throw malformed("feed", path);
}
function compact(values) {
    return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));
}
function responseRecord(value, label) {
    if (!isRecord(value))
        throw malformed(label, "object");
    return value;
}
function requiredArray(record, key, label) {
    if (!Array.isArray(record[key]))
        throw malformed(label, key);
    return record[key];
}
function requiredString(record, key, label) {
    return requiredStringFromAliases(record, [key], label);
}
function requiredStringFromAliases(record, keys, label) {
    const value = firstStringOrUndefined(record, keys);
    if (value === undefined)
        throw malformed(label, keys[0] ?? "string");
    return value;
}
function firstString(record, keys, label) {
    return requiredStringFromAliases(record, keys, label);
}
function firstStringOrUndefined(record, keys) {
    for (const key of keys) {
        if (typeof record[key] === "string" && record[key].length > 0)
            return record[key];
    }
    return undefined;
}
function optionalTimestamp(record, key, label) {
    const value = record[key];
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== "string" || value.length === 0)
        throw malformed(label, key);
    return value;
}
function firstOptionalTimestamp(record, keys, label) {
    for (const key of keys) {
        const value = optionalTimestamp(record, key, label);
        if (value !== undefined)
            return value;
    }
    return undefined;
}
function requiredBoolean(record, key, label) {
    if (typeof record[key] !== "boolean")
        throw malformed(label, key);
    return record[key];
}
function nonNegativeInteger(value, label, path) {
    if (!Number.isInteger(value) || value < 0)
        throw malformed(label, path);
    return value;
}
function parseStatus(value, allowed, label, path) {
    if (typeof value !== "string" || !allowed.includes(value))
        throw malformed(label, path);
    return value;
}
function hasOperationalFields(record) {
    return ["outbox_depth", "storage", "sync", "transport"].some((key) => key in record);
}
function malformed(label, path) {
    return new TransportError({
        code: "unavailable",
        message: `Home node returned a malformed ${label} response (${path})`,
    });
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
//# sourceMappingURL=home-node-owner-view-repository.js.map